import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { IBusiness } from "@/business/business";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { GoogleBusinessConnectionModal } from "@/business-settings/business/google-business-connection-modal";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import GoogleLogo from "@/assets/images/google-logo.svg";

type ModalState = "initial" | "location_selection" | "success" | "error";

interface GoogleBusinessPostOnboardingModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string;
  business?: IBusiness;
  onComplete?: () => void;
}

export const GoogleBusinessPostOnboardingModal = ({
  isOpen,
  onOpenChange,
  businessId,
  business,
  onComplete,
}: GoogleBusinessPostOnboardingModalProps) => {
  const { data: user } = useGetMyUser();
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>("initial");
  const [showLocationModal, setShowLocationModal] = useState(false);

  const canConnectAccount = isAdminOrOwner(user?.role);
  const isConnected =
    business?.google_business_connection_state ===
    GOOGLE_BUSINESS_CONNECTION_STATE.connected;

  useEffect(() => {
    if (isOpen && isConnected) {
      setModalState("location_selection");
    } else if (isOpen) {
      setModalState("initial");
    }
  }, [isOpen, isConnected]);

  const handleGoogleBusinessConnect = async () => {
    if (!businessId || !user?.email) {
      toast.error("Missing required information", {
        description: "Business ID and user email are required",
      });
      return;
    }

    setIsLoading(true);

    try {
      const apiBase = import.meta.env.VITE_API_URL;
      if (!apiBase) {
        toast.error("API URL not configured");
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams({
        state: JSON.stringify({
          business_id: businessId,
          user_email: user.email,
          redirect_to: "post-onboarding",
        }),
      });

      onOpenChange(false);
      window.location.href = `${apiBase}/auth/google_oauth2?${params.toString()}`;
    } catch (error) {
      console.error("Google OAuth initiation error:", error);
      toast.error("Failed to initiate Google Business connection", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
      setIsLoading(false);
      setModalState("error");
    }
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
    setModalState("success");
  };

  const handleComplete = () => {
    onComplete?.();
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  const handleRetry = () => {
    setModalState("initial");
  };

  if (!canConnectAccount && modalState === "initial") {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Google Business</DialogTitle>
          </DialogHeader>
          <Stack gap="lg" fullWidth className="p-6 pt-2">
            <Typography variant="bodyW1" textColor="tertiary">
              Only owners and admins can connect Google Business accounts.
            </Typography>
          </Stack>
          <DialogFooter>
            <Button type="button" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (showLocationModal && business) {
    return (
      <GoogleBusinessConnectionModal
        business={business}
        onClose={handleLocationModalClose}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md"
        hideCloseButton={modalState === "success"}
      >
        <DialogHeader className="border border-l-0 border-r-0 border-t-0 border-stroke-tertiary">
          <DialogTitle>Connect Google Business</DialogTitle>
        </DialogHeader>

        <Stack gap="md" fullWidth className="p-6 pt-2">
          {modalState === "initial" && (
            <>
              <Stack
                gap="md"
                align="center"
                justify="center"
                direction="row"
                fullWidth
              >
                <img src={GoogleLogo} alt="Google" className="h-8 w-8" />
                <Typography variant="bodyW1" className="text-center">
                  Connect your Google Business account
                </Typography>
              </Stack>

              <Typography
                variant="bodyW2"
                textColor="tertiary"
                className="text-center"
              >
                Connect your Google Business account so we can analyze your
                reviews and provide more insights.
              </Typography>
            </>
          )}

          {modalState === "location_selection" && (
            <>
              <Stack align="center" fullWidth>
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </Stack>
              <Typography variant="bodyW1" className="text-center">
                Google Business account connected successfully!
              </Typography>
              <Typography
                variant="bodyW2"
                textColor="tertiary"
                className="text-center"
              >
                Now select your Google Business locations to complete the setup.
              </Typography>
            </>
          )}

          {modalState === "success" && (
            <>
              <Stack align="center" fullWidth>
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </Stack>
              <Typography variant="bodyW1" className="text-center">
                Setup complete!
              </Typography>
              <Typography
                variant="bodyW2"
                textColor="tertiary"
                className="text-center"
              >
                Your Google Business account has been connected and locations
                have been added.
              </Typography>
            </>
          )}

          {modalState === "error" && (
            <>
              <Stack
                gap="md"
                align="start"
                justify="center"
                direction="row"
                fullWidth
                className="rounded-lg border border-stroke-negative bg-red-50 p-4"
              >
                <ExclamationTriangleIcon className="h-6 w-12 text-negative-text" />
                <Typography variant="bodyW1">
                  We were unable to connect to your Google Business account.
                  Please try again.
                </Typography>
              </Stack>
            </>
          )}
        </Stack>

        <DialogFooter className="w-full">
          {modalState === "initial" && (
            <>
              <Button
                type="button"
                variant="tertiary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Skip for now
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleGoogleBusinessConnect}
                disabled={!businessId || !user?.email}
                loading={isLoading}
                leftIcon={
                  <img src={GoogleLogo} alt="Google" className="h-5 w-5" />
                }
              >
                Connect Google Business
              </Button>
            </>
          )}

          {modalState === "location_selection" && (
            <>
              <Button type="button" variant="tertiary" onClick={handleComplete}>
                Skip for now
              </Button>
              <Button type="button" onClick={() => setShowLocationModal(true)}>
                Select Locations
              </Button>
            </>
          )}

          {modalState === "success" && (
            <Stack align="center" fullWidth>
              <Button type="button" onClick={handleComplete}>
                Continue
              </Button>
            </Stack>
          )}

          {modalState === "error" && (
            <>
              <Button type="button" variant="tertiary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleRetry}>
                Try Again
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
